import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import FadeIn from "@/components/FadeIn";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import axios from "axios";
import { COUNTRY_CODES, formatName, formatPhone } from "@/lib/countryCodes";
import {
  ArrowLeft, ArrowRight, CheckCircle, Shield, Users, Zap,
  Globe2, FileCheck, Handshake, BarChart3, Building2,
  Clock, Star, Bolt, UserCheck, MapPin
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const advantages = [
  { icon: FileCheck, title: "Pre-Qualified Leads", desc: "We filter inquiries, verify medical reports, and ensure financial readiness before they reach your desk." },
  { icon: Zap, title: "Zero Admin Burden", desc: "From visa assistance and airport pickups to hotel bookings and translation — we handle the logistics." },
  { icon: BarChart3, title: "Consistent Pipeline", desc: "Diversified geographic reach ensures a steady flow of international patients year-round." },
];

const howWeWork = [
  { num: "01", title: "Non-Exclusive Partnership", desc: "Value-driven relationships. Our agreements are non-exclusive, giving you freedom to maintain your other channels." },
  { num: "02", title: "Transparency & Ethics", desc: "Regular reporting and transparent patient journey tracking via our digital partner portal." },
];

const requirements = [
  { icon: Bolt, title: "Quick Medical Opinion", desc: "Treatment plans and cost estimates within 24-48 hours." },
  { icon: UserCheck, title: "Dedicated Point of Contact", desc: "An International Patient Coordinator for seamless communication." },
  { icon: Star, title: "Accreditation Standards", desc: "Commitment to JCI or NABH quality benchmarks." },
];

const regions = [
  { title: "GCC Core Focus", desc: "Marketing and partnerships in UAE, Oman, Saudi Arabia, Kuwait, Qatar, and Bahrain.", icon: Globe2 },
  { title: "East Africa Expansion", desc: "Developing referral corridors in Kenya, Ethiopia, Tanzania, Nigeria, Uganda, and Ghana.", icon: MapPin },
  { title: "Digital Precision", desc: "Multi-lingual SEO and targeted campaigns that speak the patient's language.", icon: BarChart3 },
];

export default function HospitalPartnerPage() {
  const [form, setForm] = useState({ hospital: "", location: "", contact: "", email: "", countryCode: "IN|+91", phone: "", specializations: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.phone.length < 6 || form.phone.length > 15) { toast.error("Enter a valid phone number (6-15 digits)"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: `[HOSPITAL] ${form.hospital}`,
        email: form.email,
        phone: `${form.countryCode.split("|")[1]} ${form.phone}`,
        message: `Hospital: ${form.hospital}\nLocation: ${form.location}\nContact: ${form.contact}\nPhone: ${form.countryCode.split("|")[1]} ${form.phone}\nSpecializations: ${form.specializations}\n\n${form.message}`
      });
      toast.success("Partnership inquiry submitted!");
      setSubmitted(true);
    } catch { toast.error("Failed to submit. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="partner-page" className="min-h-screen bg-white">
      <SEO title="Hospital Partners" description="Partner with Flarial MediTours to connect with international patients. Non-exclusive partnership with pre-qualified leads." path="/partners" />

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60 px-4 sm:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="partner-logo">
            <img src="/logo.png" alt="Flarial MediTours" className="w-9 h-9 object-contain" />
            <span className="font-outfit font-semibold text-lg text-[#03045e]">Flarial <span className="text-[#00A4A9]">MediTours</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-slate-500 hover:text-[#03045e] flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Back</Link>
            <a href="#partner-form"><Button size="sm" className="rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white" data-testid="partner-cta-nav">Become a Partner</Button></a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="section-padding bg-gradient-to-br from-[#03045e] via-[#023e8a] to-[#00A4A9] text-white relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-[#00A4A9]/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-[#48cae4]/20 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
            <FadeIn>
              <Badge className="mb-6 bg-white/10 text-white hover:bg-white/10 border-0 rounded-full px-4 py-1.5 text-xs">FOR HOSPITALS</Badge>
              <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-tight mb-6">
                Grow Your International Patient Base with <span className="font-medium text-[#90e0ef]">Flarial MediTours</span>
              </h1>
              <p className="text-lg text-white/70 max-w-xl mb-8">More than a facilitator — we are a strategic growth partner for hospitals in Kerala. We handle logistics, marketing, and patient coordination so you focus on clinical excellence.</p>
              <a href="#partner-form"><Button size="lg" className="rounded-full bg-white text-[#03045e] hover:bg-white/90 px-8 h-12 font-semibold" data-testid="partner-hero-cta">Start Partnership <ArrowRight className="w-4 h-4 ml-2" /></Button></a>
            </FadeIn>

            <FadeIn delay={150}>
              <div className="relative">
                {/* Large primary photo */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10" data-testid="partner-hero-photo-main">
                  <img
                    src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxob3NwaXRhbCUyMGNvb3JkaW5hdGlvbiUyMGRvY3RvcnMlMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3NzYzMTQ1NTR8MA&ixlib=rb-4.1.0&q=85"
                    alt="Kerala hospital team"
                    className="w-full h-[320px] sm:h-[400px] lg:h-[460px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#03045e]/50 via-transparent to-transparent" />
                  {/* Floating stat card */}
                  <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-md rounded-2xl p-4 shadow-xl max-w-[220px]">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-8 h-8 rounded-lg bg-[#00A4A9]/10 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-[#00A4A9]" />
                      </div>
                      <div className="font-outfit font-semibold text-[#03045e] text-sm">Pre-qualified leads</div>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">Patients are verified, funded and ready before they reach your team.</p>
                  </div>
                </div>

                {/* Two small accent photos */}
                <div className="hidden sm:grid grid-cols-2 gap-3 mt-3">
                  <div className="relative rounded-2xl overflow-hidden shadow-lg ring-1 ring-white/10" data-testid="partner-hero-photo-small-1">
                    <img
                      src="https://images.unsplash.com/photo-1758691462119-792279713969?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxkb2N0b3IlMjBwYXRpZW50JTIwY29uc3VsdGF0aW9uJTIwaG9zcGl0YWx8ZW58MHx8fHwxNzc2MjUyMjY4fDA&ixlib=rb-4.1.0&q=85"
                      alt="Doctor consultation"
                      className="w-full h-32 object-cover"
                    />
                  </div>
                  <div className="relative rounded-2xl overflow-hidden shadow-lg ring-1 ring-white/10" data-testid="partner-hero-photo-small-2">
                    <img
                      src="https://images.unsplash.com/photo-1725859313485-bc19c4c79c13?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxhaXJwb3J0JTIwd2VsY29tZSUyMHBhdGllbnQlMjBtZWRpY2FsJTIwdG91cmlzbXxlbnwwfHx8fDE3NzYyNTIyNTR8MA&ixlib=rb-4.1.0&q=85"
                      alt="International patient arrival"
                      className="w-full h-32 object-cover"
                    />
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Global Footprint */}
      <section className="section-padding bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto">
          <FadeIn><div className="text-center mb-12"><span className="overline">OUR REACH</span><h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3">Global Patient Footprint</h2></div></FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {regions.map((r, i) => (
              <FadeIn key={r.title} delay={i * 80}>
                <div className="bg-white rounded-2xl p-7 border border-slate-200 card-hover shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`region-card-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-[#e0f2fe] flex items-center justify-center mb-4"><r.icon className="w-6 h-6 text-[#00A4A9]" /></div>
                  <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-2">{r.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{r.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeIn><div className="text-center mb-12"><span className="overline">THE ADVANTAGE</span><h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3">Why Partner With Us</h2></div></FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {advantages.map((a, i) => (
              <FadeIn key={a.title} delay={i * 80}>
                <div className="bg-[#F9F9F8] rounded-2xl p-8 border border-slate-200" data-testid={`advantage-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-[#00A4A9] flex items-center justify-center mb-5"><a.icon className="w-6 h-6 text-white" /></div>
                  <h3 className="font-outfit text-xl font-medium text-[#03045e] mb-3">{a.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{a.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="section-padding bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto">
          <FadeIn><div className="text-center mb-12"><span className="overline">HOW IT WORKS</span><h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3">How We Work Together</h2></div></FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howWeWork.map((s, i) => (
              <FadeIn key={s.num} delay={i * 100}>
                <div className="text-center" data-testid={`how-work-${i}`}>
                  <div className="w-14 h-14 rounded-2xl bg-[#00A4A9] flex items-center justify-center mx-auto mb-5"><span className="font-outfit text-lg font-bold text-white">{s.num}</span></div>
                  <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-2">{s.title}</h3>
                  <p className="text-slate-500 text-sm">{s.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeIn><div className="text-center mb-12"><span className="overline">WHAT WE NEED</span><h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3">What We Need From You</h2></div></FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {requirements.map((r, i) => (
              <FadeIn key={r.title} delay={i * 80}>
                <div className="flex gap-4 items-start bg-[#F9F9F8] rounded-2xl p-6 border border-slate-200" data-testid={`requirement-${i}`}>
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#e0f2fe] flex items-center justify-center"><r.icon className="w-5 h-5 text-[#00A4A9]" /></div>
                  <div><h4 className="font-outfit font-medium text-[#03045e] mb-1">{r.title}</h4><p className="text-sm text-slate-500">{r.desc}</p></div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Transparency */}
      <section className="section-padding bg-[#03045e] text-white">
        <div className="max-w-5xl mx-auto text-center">
          <FadeIn>
            <h2 className="font-outfit text-3xl sm:text-4xl font-medium mb-8">Transparency is Our Foundation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-2xl p-7 border border-white/10">
                <Shield className="w-8 h-8 text-[#90e0ef] mx-auto mb-4" />
                <h3 className="font-outfit text-lg font-medium mb-2">Direct Hospital Billing</h3>
                <p className="text-sm text-white/60">We don't markup treatment costs. Patients pay the hospital directly at your official international rates.</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-7 border border-white/10">
                <Handshake className="w-8 h-8 text-[#90e0ef] mx-auto mb-4" />
                <h3 className="font-outfit text-lg font-medium mb-2">Patient-First Advocacy</h3>
                <p className="text-sm text-white/60">Our coordinators are healthcare professionals, not sales agents. Patient health outcomes are our only KPI.</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Partner Form */}
      <section id="partner-form" className="section-padding bg-[#F9F9F8]">
        <div className="max-w-2xl mx-auto">
          <FadeIn>
            <div className="text-center mb-10">
              <span className="overline">GET STARTED</span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3">Partner With Us</h2>
              <p className="text-slate-600 mt-3">Fill out the form and our Partnership Manager will reach out.</p>
            </div>
            {submitted ? (
              <div className="text-center bg-white rounded-2xl p-12 border border-slate-200">
                <CheckCircle className="w-12 h-12 text-[#00A4A9] mx-auto mb-4" />
                <h3 className="font-outfit text-2xl font-medium text-[#03045e] mb-3">Thank You!</h3>
                <p className="text-slate-600 mb-6">We've received your partnership inquiry. Our team will contact you within 48 hours.</p>
                <Button onClick={() => setSubmitted(false)} className="rounded-full bg-[#00A4A9] text-white" data-testid="partner-again-btn">Submit Another Inquiry</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="partner-form-element">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div><Label className="text-sm text-slate-700 mb-1.5 block">Hospital Name *</Label><Input required value={form.hospital} onChange={(e) => setForm({ ...form, hospital: e.target.value })} placeholder="KIMS Hospital" data-testid="partner-hospital" /></div>
                  <div><Label className="text-sm text-slate-700 mb-1.5 block">Location *</Label><Input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Kochi, Kerala" data-testid="partner-location" /></div>
                  <div><Label className="text-sm text-slate-700 mb-1.5 block">Contact Person *</Label><Input required value={form.contact} onChange={(e) => setForm({ ...form, contact: formatName(e.target.value) })} placeholder="Dr. Kumar" data-testid="partner-contact" /></div>
                  <div><Label className="text-sm text-slate-700 mb-1.5 block">Work Email *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="admin@hospital.com" data-testid="partner-email" /></div>
                </div>
                <div className="mb-5">
                  <Label className="text-sm text-slate-700 mb-1.5 block">Phone *</Label>
                  <div className="flex gap-2">
                    <select
                      required
                      value={form.countryCode}
                      onChange={(e) => setForm({ ...form, countryCode: e.target.value })}
                      className="h-10 w-28 flex-shrink-0 rounded-lg border border-slate-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A4A9] bg-white"
                      data-testid="partner-country-code"
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={`${c.code}|${c.dial}`}>{c.dial} {c.name}</option>
                      ))}
                    </select>
                    <Input required inputMode="numeric" value={form.phone} onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })} placeholder="9876543210" data-testid="partner-phone" />
                  </div>
                </div>
                <div className="mb-5"><Label className="text-sm text-slate-700 mb-1.5 block">Key Specializations *</Label><Input required value={form.specializations} onChange={(e) => setForm({ ...form, specializations: e.target.value })} placeholder="Cardiology, Orthopedics, Oncology" data-testid="partner-spec" /></div>
                <div className="mb-6"><Label className="text-sm text-slate-700 mb-1.5 block">Message (Optional)</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your hospital..." className="min-h-[100px]" data-testid="partner-message" /></div>
                <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white h-12" data-testid="partner-submit-btn">
                  {loading ? "Submitting..." : "Submit Partnership Interest"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>
            )}
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#03045e] text-white py-8 px-4 text-center">
        <p className="text-sm text-white/40">&copy; {new Date().getFullYear()} Flarial MediTours. All rights reserved.</p>
      </footer>
    </div>
  );
}
